import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';

import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { CircularProgress, Button, Grid, FormControl, Input, InputLabel,
         MenuItem, NativeSelect, Paper,  Select, 
         TableContainer, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import CheckCircle from '@mui/icons-material/CheckCircle';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';
import { ZoomIn } from '@mui/icons-material';
import Cancel from '@mui/icons-material/Cancel';


import AbEnvironment from '../auth/AbEnvironment';
import Address from '../ui/Address'

import BusinessIdentityUpdateMutation from './BusinessIdentityUpdateMutation';
import AccountPaymentCidUpdateMutation from '../account/AccountPaymentCidUpdateMutation';

import BackButton from '../ui/BackButton';
import HelpOutline from '@mui/icons-material/HelpOutline';
import PageTitle from '../ui/PageTitle';
import ConfirmationDialog from '../ui/ConfirmationDialog';
import CountrySelect from '../ui/CountrySelect'
import PhoneNumberFormat from '../ui/PhoneNumberFormat';
import SaveButton from '../ui/SaveButton';
import AppBindStyles from '../../AppBindStyles';
import LoadingSpinner from '../ui/LoadingSpinner';
import SavingSpinner from '../ui/SavingSpinner';
import ServerError from '../ui/ServerError';

import { green, red, amber } from '@mui/material/colors';
import { differenceInYears, format, subYears } from 'date-fns';

const abEnvironment = new AbEnvironment();
const BusinessIdentityEditQuery = graphql`
query BusinessIdentityEditQuery($biId: UUID!) {
  businessIdentityTByBiId(biId: $biId) {   
    accountId
    businessName
    doingBusinessAs
    businessType
    businessClassification
    website
    ein
    ipAddress
    ssn
    dateOfBirth
    contactLastName
    contactFirstName
    contactEmail
    contactPhone
    address1
    address2
    city
    state
    country
    postalCode
    ctrTitle
    ctrSsn
    ctrDateOfBirth
    ctrPassportNumber
    ctrPassportCountry
    ctrLastName
    ctrFirstName
    ctrAddress1 
    ctrAddress2 
    ctrCity
    ctrState
    ctrCountry
    ctrPostalCode
    beneficialOwnerTsByBiId {
      totalCount
      nodes {
        boId
        firstName
        lastName
        dwollaId
      }
    }
    accountTByAccountId {
      dwollaCid
    }
    businessIdentityDocTsByBiId {
      totalCount
      nodes {
        bidId
        dwollaId
        dwollaStatus
        documentType
        created
      }
    }
  }
}
`;
const styleSheet = theme => ({
    root: {
      flexGrow: 1,
    },
    flex: {
      flex: 1,
    },
    paper: {
      padding: theme.spacing(3),
      textAlign: 'left',
      color: theme.palette.text.secondary,
      maxWidth: 980,
      marginTop: 16
    },
    success: {
      backgroundColor: green[300],
    },
    error: {
      backgroundColor: theme.palette.error.light,
    },
    message: {
      display: 'flex',
      alignItems: 'center',
    },
    close: {
      padding: theme.spacing(0.5),
    },
    select: {
      marginLeft: -8,
      marginTop: -8,
    },
    dateField: {
      width: 160,
    },
    businessClassification: {
      margin: theme.spacing(1),
    }  
});

class BusinessIdentityEdit extends Component {
    state = {
        showSpinner: false,
        showSecondarySpinner: false,
        saveStatus: null,
        saveMessage: null,
        snackbarOpen: false,
        confirmSend: false,    
        forceFetch: 0,        
        dwollaFetchDone: false,

        businessType: null,
        businessName: null,
        doingBusinessAs: null,
        website: null,
        ein: null,
        ipAddress: null,
        businessClassification: null,
        
        contactLastName: null,
        contactFirstName: null,
        contactEmail: null,
        contactPhone: null,
        dateOfBirth: null,
        ssn: null,

        address1: null,
        address2: null,
        city: null,
        state: null,
        country: null,
        postalCode: null,

        ctrTitle: null,
        ctrSsn: null, 
        ctrDateOfBirth: null,
        ctrPassportNumber: null,
        ctrPassportCountry: null,
        ctrLastName: null, 
        ctrFirstName: null, 
        ctrAddress1: null, 
        ctrAddress2: null, 
        ctrCity: null, 
        ctrState: null, 
        ctrCountry: null, 
        ctrPostalCode: null,
        dwollaStatus: null,
        dwollaList: [],
        existingDwollaCust: null,
        certifyOwnersNeeded: false,
        certifyOwnersDialog: false

    }
    componentDidMount() {
      //if (abEnvironment.isSegmentActive()) { window.analytics.page(); }
      this.getDwollaBusinessClassifications();
      this.getDwollaCust();

      let success = localStorage.getItem('vsuccess');
      //if success have a value, it means coming from business entity or document upload and we need to show message
      if (success && success === 'ss') {
        this.setState({ saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Save successful.', saveSnackbarDuration: 3000 });
      } else if (success && success === 'pn') {
        this.setState({ saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Partner notified.', saveSnackbarDuration: 3000 });
      } else if (success && success === 'du') {
        this.setState({ saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Document uploaded.', saveSnackbarDuration: 3000 });
      } 
      localStorage.removeItem('vsuccess');
    }


    getDwollaBusinessClassifications() {
      const { callPartnerApi, logError } = this.props.auth;
   
      let aPjwt = abEnvironment.getPjwt();
  
      // Call server side ach form prep
      let payload = {
        operation: "listDwollaBusinessClassifications",
        params: {
          pJwt:      aPjwt
        }
      };
  
      callPartnerApi('appbind/generic',
                     'POST',
                     payload
      ).then(resp => {
        return resp.json();
      }).then(data => {


        let businessClassifications = data.body._embedded["business-classifications"];
        let extractedBc = [];
         for (var i = 0; i < businessClassifications.length; i++) {
          let industryName = businessClassifications[i].name;

          let industryClassifications = businessClassifications[i]._embedded["industry-classifications"];
          for (var y = 0; y < industryClassifications.length; y++) {
            let business = industryClassifications[y];

            let entry = {"id": business.id, "name": industryName + ' - ' + business.name};
            extractedBc.push(entry);
  
          }
  
        }
        this.setState({dwollaList: extractedBc});

      }).catch(reason => {
         logError('Exception in BusinessIdentityEdit.js: ' + reason);
      });      
    }

    getDwollaCust() {
      const { callPartnerApi, logError } = this.props.auth;
   
      let aPjwt = abEnvironment.getPjwt();
      const params = new URLSearchParams(this.props.location.search);
      let dwollaCId = params.get('dId');

      if (dwollaCId) {
        //retrieve for customer
        let payload = {
          operation: "getDwollaCustomer",
          params: {
            pJwt:      aPjwt,
            dwollaCId: dwollaCId
          }
        };
    
        callPartnerApi('appbind/generic',
                       'POST',
                       payload
        ).then(resp => {
          return resp.json();
        }).then(data => {
  
          //console.log(JSON.stringify(data.body));

          //console.log(data.body._links);
          //console.log(data.body._links["certify-beneficial-ownership"]);

          let certifyOwnersNeeded = false;
          if (data.body._links) {
            if (data.body._links["certify-beneficial-ownership"]) {
              //console.log(data.body._links["certify-beneficial-ownership"]);    
              certifyOwnersNeeded = true;
            }
          }

          this.setState({dwollaFetchDone: true, existingDwollaCust: data.body, certifyOwnersNeeded: certifyOwnersNeeded})
  
        }).catch(reason => {
          logError('Exception in BusinessIdentityEdit.js: ' + reason);

          this.setState({dwollaFetchDone: true});   
        });      
  
      } else {
        this.setState({dwollaFetchDone: true});
      }
    }

    handleBusinessChange = (event) => {
      this.setState({businessType: event.target.value})
    };

    goBack() {
      this.props.history.goBack();
    }
    
    goTo(route) {
      this.props.history.push(`/${route}`);
    }
  
    replaceRoute(route) {
      this.props.history.replace(`/${route}`);
    }

    goToBeneficialOwnerCreate(biId) {
      let url = 'businessbeneficialownercreate?biId=' + biId; 
      this.props.history.push(`/${url}`);
    }

    goToDocumentDetail = (bidId, dwollaId) => {
      let url = 'documentviewer?bidId=' + bidId + '&dId=' + dwollaId;
      this.props.history.push(`/${url}`);
    }

    
    goToDocumentUpload = (biId) => {
      let url = 'documentupload?biId=' + biId + '&type=be';
      this.props.history.push(`/${url}`);
  
    }

    getValueForRender(state_member, prop_member, theDefault)
    {
       if (state_member != null)
        return state_member;
      
      if (prop_member != null)
        return prop_member;
  
      return theDefault;
    }


    showSpinner() {
        return this.state.showSpinner;
    }


    showSecondarySpinner() {
      return this.state.showSecondarySpinner;
    }

    disableSave(renderProps) {

      const { validateEmail } = this.props.auth;

      if (renderProps === null || renderProps.businessIdentityTByBiId === null)
        return true;
 
      let retval = this.disableAttributeCheck(this.state.businessName, renderProps.businessIdentityTByBiId.businessName) &&
                   this.disableAttributeCheck(this.state.doingBusinessAs, renderProps.businessIdentityTByBiId.doingBusinessAs) &&
                   this.disableAttributeCheck(this.state.businessType, renderProps.businessIdentityTByBiId.businessType) &&
                   this.disableAttributeCheck(this.state.businessClassification, renderProps.businessIdentityTByBiId.businessClassification) &&
                   this.disableAttributeCheck(this.state.website, renderProps.businessIdentityTByBiId.website) &&
                   this.disableAttributeCheck(this.state.ein, renderProps.businessIdentityTByBiId.ein) &&
                   this.disableAttributeCheck(this.state.ipAddress, renderProps.businessIdentityTByBiId.ipAddress) &&
                   this.disableAttributeCheck(this.state.ssn, renderProps.businessIdentityTByBiId.ssn) &&
                   this.disableAttributeCheck(this.state.dateOfBirth, renderProps.businessIdentityTByBiId.dateOfBirth) &&
                   this.disableAttributeCheck(this.state.contactLastName, renderProps.businessIdentityTByBiId.contactLastName) &&
                   this.disableAttributeCheck(this.state.contactFirstName, renderProps.businessIdentityTByBiId.contactFirstName) &&
                   this.disableAttributeCheck(this.state.contactEmail, renderProps.businessIdentityTByBiId.contactEmail) &&
                   this.disableAttributeCheck(this.state.contactPhone, renderProps.businessIdentityTByBiId.contactPhone) &&
                   this.disableAttributeCheck(this.state.address1, renderProps.businessIdentityTByBiId.address1) &&
                   this.disableAttributeCheck(this.state.address2, renderProps.businessIdentityTByBiId.address2) &&
                   this.disableAttributeCheck(this.state.city, renderProps.businessIdentityTByBiId.city) &&
                   this.disableAttributeCheck(this.state.state, renderProps.businessIdentityTByBiId.state) &&
                   this.disableAttributeCheck(this.state.country, renderProps.businessIdentityTByBiId.country) &&
                   this.disableAttributeCheck(this.state.postalCode, renderProps.businessIdentityTByBiId.postalCode) &&
                   this.disableAttributeCheck(this.state.ctrTitle, renderProps.businessIdentityTByBiId.ctrTitle) &&
                   this.disableAttributeCheck(this.state.ctrSsn, renderProps.businessIdentityTByBiId.ctrSsn) &&
                   this.disableAttributeCheck(this.state.ctrDateOfBirth, renderProps.businessIdentityTByBiId.ctrDateOfBirth) &&
                   this.disableAttributeCheck(this.state.ctrPassportNumber, renderProps.businessIdentityTByBiId.ctrPassportNumber) &&
                   this.disableAttributeCheck(this.state.ctrPassportCountry, renderProps.businessIdentityTByBiId.ctrPassportCountry) &&
                   this.disableAttributeCheck(this.state.ctrLastName, renderProps.businessIdentityTByBiId.ctrLastName) &&
                   this.disableAttributeCheck(this.state.ctrFirstName, renderProps.businessIdentityTByBiId.ctrFirstName) &&
                   this.disableAttributeCheck(this.state.ctrAddress1, renderProps.businessIdentityTByBiId.ctrAddress1) &&
                   this.disableAttributeCheck(this.state.ctrAddress2, renderProps.businessIdentityTByBiId.ctrAddress2) &&
                   this.disableAttributeCheck(this.state.ctrCity, renderProps.businessIdentityTByBiId.ctrCity) &&
                   this.disableAttributeCheck(this.state.ctrState, renderProps.businessIdentityTByBiId.ctrState) &&
                   this.disableAttributeCheck(this.state.ctrCountry, renderProps.businessIdentityTByBiId.ctrCountry) &&
                   this.disableAttributeCheck(this.state.ctrPostalCode, renderProps.businessIdentityTByBiId.ctrPostalCode);
 

      //make sure user did not blank out anything required
      if (this.state.businessName === '')
        return true;

      if (this.state.doingBusinessAs === '')
        return true;

      if (this.state.businessClassification === '')
        return true;
       
      if (this.state.contactLastName === '')
        return true;

      if (this.state.contactFirstName === '')
        return true;

      if (this.state.contactEmail === '')
        return true;

      if (this.state.address1 === '')
        return true;

      if (this.state.city === '')
        return true;

      if (this.state.state === '')
        return true;

      if (this.state.country === '')
        return true;

      if (this.state.postalCode === '')
        return true;
  
      // Is email an email address?
      if (this.state.contactEmail) {
        let emailOk = validateEmail(this.state.contactEmail);
        if (! emailOk)
          return true;

      }


      //now do fields special for each type
      if (this.getValueForRender(this.state.businessType, renderProps.businessIdentityTByBiId.businessType, null) === 'soleProprietorship') {
        if (this.state.ssn === '') 
          return true;       

        if (this.state.dateOfBirth === '')
          return true;

        const age = differenceInYears(new Date(), new Date(this.getValueForRender(this.state.dateOfBirth, renderProps.businessIdentityTByBiId.dateOfBirth, null)));
        if (age < 18) {
          return true;
        }
              

      } else {
        if (this.state.ein === '')
          return true;

          //validate ein is 9
          var re = /^\d{9}$/;
          if (!re.test(this.getValueForRender(this.state.ein, renderProps.businessIdentityTByBiId.ein, null))) {
            return true;
          }
           

          if (this.getValueForRender(this.state.ctrCountry, renderProps.businessIdentityTByBiId.ctrCountry, null) === "US") {  
              if (this.state.ctrSsn === '') {
                  return true;
              }
          }

        if (this.state.ctrTitle === '')
          return true;

        if (this.state.ctrDateOfBirth === '')
          return true;

          const age = differenceInYears(new Date(), new Date(this.getValueForRender(this.state.ctrDateOfBirth, renderProps.businessIdentityTByBiId.ctrDateOfBirth, null)));
          if (age < 18) {
            return true;
          }

          if (this.state.ctrAddress1 === '')
          return true;
  
        if (this.state.ctrCity === '')
          return true;
  
        if (this.state.ctrState === '')
          return true;
  
        if (this.state.ctrCountry === '')
          return true;
  
        if (this.state.ctrPostalCode === '')
          return true;
  
        if (this.getValueForRender(this.state.ctrCountry, renderProps.businessIdentityTByBiId.ctrCountry, null) !== "US") {
          if (this.state.ctrPassportCountry === '' || this.state.ctrPassportCountry === null)
            return true;
          if (this.state.ctrPassportNumber === '' || this.state.ctrPassportNumber === null)
            return true;
          
        }

      }


      if (this.state.businessType === 'soleProprietorship' && this.state.businessType !== renderProps.businessIdentityTByBiId.businessType) {
        //user changed business type, make sure fields are filled
        let ssn = this.getValueForRender(this.state.ssn, renderProps.businessIdentityTByBiId.ssn, null);
        if (ssn === null || ssn === '') 
          return true;       

        let dob = this.getValueForRender(this.state.dateOfBirth, renderProps.businessIdentityTByBiId.dateOfBirth, null);
        if (dob === null || dob === '') 
          return true;
              
        const age = differenceInYears(new Date(), new Date(this.getValueForRender(this.state.dateOfBirth, renderProps.businessIdentityTByBiId.dateOfBirth, null)));
        if (age < 18) {
          return true;
        }
  
      } else if (this.state.businessType !== null && this.state.businessType !== renderProps.businessIdentityTByBiId.businessType) {
        //user changed business type, make sure required fields are filled

        let ein = this.getValueForRender(this.state.ein, renderProps.businessIdentityTByBiId.ein, null);
        if (ein === null || ein === '') 
          return true;

        //validate ein is 9
        var einRe = /^\d{9}$/;
        if (!einRe.test(this.getValueForRender(this.state.ein, renderProps.businessIdentityTByBiId.ein, null))) {
          return true;
        }


        if (this.getValueForRender(this.state.ctrCountry, renderProps.businessIdentityTByBiId.ctrCountry, null) === "US") {  
         let ctrSsn = this.getValueForRender(this.state.ctrSsn, renderProps.businessIdentityTByBiId.ctrSsn, null);
         if (ctrSsn === null || ctrSsn === '') 
           return true;
        }

        let ctrTitle = this.getValueForRender(this.state.ctrTitle, renderProps.businessIdentityTByBiId.ctrTitle, null);
        if (ctrTitle === null || ctrTitle === '') 
          return true;

        let ctrDateOfBirth = this.getValueForRender(this.state.ctrDateOfBirth, renderProps.businessIdentityTByBiId.ctrDateOfBirth, null);
        if (ctrDateOfBirth === null || ctrDateOfBirth === '') 
          return true;

        const age = differenceInYears(new Date(), new Date(this.getValueForRender(this.state.ctrDateOfBirth, renderProps.businessIdentityTByBiId.ctrDateOfBirth, null)));
        if (age < 18) {
          return true;
        }

        let ctrAddress1 = this.getValueForRender(this.state.ctrAddress1, renderProps.businessIdentityTByBiId.ctrAddress1, null);
        if (ctrAddress1 === null || ctrAddress1 === '') 
          return true;

        let ctrCity = this.getValueForRender(this.state.ctrCity, renderProps.businessIdentityTByBiId.ctrCity, null);
        if (ctrCity === null || ctrCity === '') 
          return true;

        let ctrState = this.getValueForRender(this.state.ctrState, renderProps.businessIdentityTByBiId.ctrState, null);
        if (ctrState === null || ctrState === '') 
          return true;

        let ctrCountry = this.getValueForRender(this.state.ctrCountry, renderProps.businessIdentityTByBiId.ctrCountry, null);
        if (ctrCountry === null || ctrCountry === '') 
          return true;

        let ctrPostalCode = this.getValueForRender(this.state.ctrPostalCode, renderProps.businessIdentityTByBiId.ctrPostalCode, null);
        if (ctrPostalCode === null || ctrPostalCode === '') 
          return true;

        if (this.getValueForRender(this.state.ctrCountry, renderProps.businessIdentityTByBiId.ctrCountry, null) !== "US") {
          let ctrPassportCountry = this.getValueForRender(this.state.ctrPassportCountry, renderProps.businessIdentityTByBiId.ctrPassportCountry, null);
          if (ctrPassportCountry === null || ctrPassportCountry === '') 
            return true;

          let ctrPassportNumber = this.getValueForRender(this.state.ctrPassportNumber, renderProps.businessIdentityTByBiId.ctrPassportNumber, null);
          if (ctrPassportNumber === null || ctrPassportNumber === '') 
            return true; 
        }

      }

      //console.log('from disableSave() ' + retval);
      return retval;
    }

    disableAttributeCheck(stateVar, propsVar)
    {
        if (stateVar !== null)
        {
            if (propsVar === null && stateVar === '')
                return true;
            
            if (propsVar !== stateVar)
                return false;
        }
        return true;
    }
 
    setCountry    = (country)    => { this.setState({country:    country}); }  
    setRegion     = (region)     => { this.setState({state:      region}); }  
    setCity       = (city)       => { this.setState({city:       city}); }  
    setPostalCode = (postalCode) => { this.setState({postalCode: postalCode}); }  
    setAddress1   = (address1)   => { this.setState({address1:   address1}); }  
    setAddress2   = (address2)   => { this.setState({address2:   address2}); }  

    setCtrCountry    = (country)    => { this.setState({ctrCountry:    country}); }  
    setCtrRegion     = (region)     => { this.setState({ctrState:      region}); }  
    setCtrCity       = (city)       => { this.setState({ctrCity:       city}); }  
    setCtrPostalCode = (postalCode) => { this.setState({ctrPostalCode: postalCode}); }  
    setCtrAddress1   = (address1)   => { this.setState({ctrAddress1:   address1}); }  
    setCtrAddress2   = (address2)   => { this.setState({ctrAddress2:   address2}); }  
    setPassportCountry    = (country)    => { this.setState({ctrPassportCountry:    country}); }  

    sendDwollaInfo = (biId, props) => {
      
      if (!this.state.existingDwollaCust) {
        this.sendDwollaInfoNew(biId, props);
      } else {
        this.sendDwollaInfoExisting(biId, props);
      }
      

    }

    sendDwollaInfoExisting = (biId, props) => {

      this.setState({ showSecondarySpinner: true });
      const { callPartnerApi, logError } = this.props.auth;
      let aPjwt = abEnvironment.getPjwt();

      let payload =  {
          operation: "updateDwollaBusinessCustomer",
          params: {
            pJwt: aPjwt,
            dwollaCId: this.state.existingDwollaCust.id,
            requestBody: this.getRequestBodyForBusinessVerification(props)
          }
        }
      
      callPartnerApi('appbind/generic',
                      'POST',
                      payload
      )
      .then((response) => {
        return response.json();
      })  
    .then(response => {

        //console.log(JSON.stringify(response));
        if (response.err) {
          if (response.err.status === 404) {
            throw new Error('Error occurred notifying our partner, please contact AppBind support');
          }
          //if this happens it is an error trapped by dwolla either new or that we don't trap for - lets show it.
          let errorMsg = response.err.body._embedded.errors[0].message;
          throw (errorMsg);
        }

        let fetchCounter = this.state.forceFetch;
        fetchCounter++;
        this.setState({ forceFetch: fetchCounter, showSecondarySpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Partner has been updated.', saveSnackbarDuration: 3000 });
        window.location.reload();
 
      })
      .catch(error => {
        logError('Exception in BusinessIdentifyEdit.js: ' + error);

        let errMsg = error;
        if (!errMsg || errMsg === '') {
          errMsg = 'Problem notifying our ACH partner.';
        }
        this.setState({ showSecondarySpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: errMsg, saveSnackbarDuration: null  });
      });

    }

    sendOwnerCerification = (biId) => {

      this.setState({ showSecondarySpinner: true });
      const { callPartnerApi, logError } = this.props.auth;
      let aPjwt = abEnvironment.getPjwt();

      let payload =  {
          operation: "sendDwollaOwnerCertification",
          params: {
            pJwt: aPjwt,
            dwollaCId: this.state.existingDwollaCust.id,
            requestBody: {status: "certified"}
          }
        }
      
      callPartnerApi('appbind/generic',
                      'POST',
                      payload
      )
      .then((response) => {
        return response.json();
      })  
    .then(response => {

 
        //console.log(JSON.stringify(response));
        if (response.err) {
          if (response.err.status === 404) {
            throw new Error('Error occurred notifying our partner, please contact AppBind support');
          }
          if ({"abMessage":"Error in generic call","err":"No operation found","data":null})
          //if this happens it is an error trapped by dwolla either new or that we don't trap for - lets show it.
          if (response.err.body) {
            let errorMsg = response.err.body._embedded.errors[0].message;
            throw (errorMsg);
          } else {
            console.log(JSON.stringify(response.err));
            let errorMsg = 'An error occurred.  Please call AppBind support.';
            throw (errorMsg);
          }
        }

        let fetchCounter = this.state.forceFetch;
        fetchCounter++;
        this.setState({ forceFetch: fetchCounter, showSecondarySpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Partner has been updated.', saveSnackbarDuration: 3000 });
        window.location.reload();
      })
      .catch(error => {
        logError('Exception in BusinessIdentifyEdit.js: ' + error);
        let errMsg = error;
        if (!errMsg || errMsg === '') {
          errMsg = 'Problem notifying our ACH partner.';
        }
        this.setState({ showSecondarySpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: errMsg, saveSnackbarDuration: null  });
      });

    }


    sendDwollaInfoNew = (biId, props) => {

      this.setState({ showSecondarySpinner: true });
      const { callPartnerApi, logError } = this.props.auth;
      let accountId = abEnvironment.getUserAttribute("a_id");
      let aPjwt = abEnvironment.getPjwt();
      let dwollaId = null;

      let payload = {
          operation: "createDwollaBusinessVerifiedCustomer",
          params: {
            pJwt: aPjwt,
            requestBody: this.getRequestBodyForBusinessVerification(props)
          }
      }
      
      
      callPartnerApi('appbind/generic',
                      'POST',
                      payload
      )
      .then((response) => {
        return response.json();
      })  
      .then(response => {

        if (response.err) {
          if (response.err.status === 404) {
            throw new Error('Error occurred notifying our partner, please contact AppBind support');
          }
          //if this happens it is an error trapped by dwolla either new or that we don't trap for - lets show it.
          let errorMsg = response.err.body._embedded.errors[0].message;
          throw (errorMsg);
        }

        dwollaId = response.id;

        AccountPaymentCidUpdateMutation.commit(accountId, null, dwollaId);

       })
      .then(response => {

        localStorage.setItem('vsuccess', 'pn')
        let url = 'businessidentityedit?biId=' + biId + '&dId=' + dwollaId;
        this.replaceRoute(url); 
        window.location.reload();
      })
      .catch(error => {
        logError('Exception in BusinessIdentifyEdit.js: ' + error);
        let errMsg = error;
        if (!errMsg || errMsg === '') {
          errMsg = 'Problem notifying our ACH partner.';
        }
        this.setState({ showSecondarySpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: errMsg, saveSnackbarDuration: null  });
      });

    }

    getRequestBodyForBusinessVerification = (props) => {

      var requestBody = {};
      if (this.state.existingDwollaCust) {
        requestBody.firstName = this.state.existingDwollaCust.firstName;
        requestBody.lastName = this.state.existingDwollaCust.lastName;
        requestBody.email = this.state.existingDwollaCust.email;

      } else {
        requestBody.firstName = props.businessIdentityTByBiId.contactFirstName;
        requestBody.lastName = props.businessIdentityTByBiId.contactLastName;
        requestBody.email = props.businessIdentityTByBiId.contactEmail;
      }
      requestBody.type = 'business';

      if (props.businessIdentityTByBiId.businessType === 'soleProprietorship') {
        //sole proprietorship
        if (!this.state.existingDwollaCust || !this.state.existingDwollaCust.status === 'verified') {
          //todo figure out what sole propietorship allows to change.
        }

        requestBody.dateOfBirth = props.businessIdentityTByBiId.dateOfBirth;
        requestBody.ssn = props.businessIdentityTByBiId.ssn;
        requestBody.address1 = props.businessIdentityTByBiId.address1;
        requestBody.city = props.businessIdentityTByBiId.city;
        requestBody.state = props.businessIdentityTByBiId.state;
        requestBody.postalCode = props.businessIdentityTByBiId.postalCode;
        if (props.businessIdentityTByBiId.contactPhone) {
          requestBody.phone = props.businessIdentityTByBiId.contactPhone;
        }
        requestBody.businessClassification = props.businessIdentityTByBiId.businessClassification;
        requestBody.businessType = props.businessIdentityTByBiId.businessType;
        requestBody.businessName = props.businessIdentityTByBiId.businessName;
        if (props.businessIdentityTByBiId.doingBusinessAs) {
          requestBody.doingBusinessAs = props.businessIdentityTByBiId.doingBusinessAs;
        }
      } else {
        //llc, etc proprietorship
        requestBody.address1 = props.businessIdentityTByBiId.address1;
        requestBody.city = props.businessIdentityTByBiId.city;
        requestBody.state = props.businessIdentityTByBiId.state;
        requestBody.postalCode = props.businessIdentityTByBiId.postalCode;
        if (props.businessIdentityTByBiId.contactPhone) {
          requestBody.phone = props.businessIdentityTByBiId.contactPhone;
        }
        if (props.businessIdentityTByBiId.doingBusinessAs) {
          requestBody.doingBusinessAs = props.businessIdentityTByBiId.doingBusinessAs;
        }
        if (props.businessIdentityTByBiId.website) {
          requestBody.website = props.businessIdentityTByBiId.website;
        }
        if (props.businessIdentityTByBiId.contactPhone) {
          requestBody.phone = props.businessIdentityTByBiId.contactPhone;
        }

        if (!this.state.existingDwollaCust || this.state.existingDwollaCust.status !== 'verified') {
          requestBody.businessName = props.businessIdentityTByBiId.businessName;
          requestBody.businessType = props.businessIdentityTByBiId.businessType;
          requestBody.businessClassification = props.businessIdentityTByBiId.businessClassification;
          requestBody.ein = props.businessIdentityTByBiId.ein;

          //this is not part of Dwolla documentation but added after discovering a bug.  It is needed
          //in case up change from sole p -> something else but they said we could always put it in.
          requestBody.ssn = props.businessIdentityTByBiId.ctrSsn;

          requestBody.controller = {};
          requestBody.controller.firstName = props.businessIdentityTByBiId.ctrFirstName;
          requestBody.controller.lastName = props.businessIdentityTByBiId.ctrLastName;
          requestBody.controller.title = props.businessIdentityTByBiId.ctrTitle;
          requestBody.controller.dateOfBirth = props.businessIdentityTByBiId.ctrDateOfBirth;
          if (props.businessIdentityTByBiId.ctrSsn) {
            requestBody.controller.ssn = props.businessIdentityTByBiId.ctrSsn;
          }
          requestBody.controller.address = {};
          requestBody.controller.address.address1 = props.businessIdentityTByBiId.ctrAddress1;
          if (props.businessIdentityTByBiId.ctrAddress2) {
            requestBody.controller.address.address2 = props.businessIdentityTByBiId.ctrAddress2;
          }
          requestBody.controller.address.city = props.businessIdentityTByBiId.ctrCity;
          requestBody.controller.address.stateProvinceRegion = props.businessIdentityTByBiId.ctrState;
          requestBody.controller.address.postalCode = props.businessIdentityTByBiId.ctrPostalCode;
          requestBody.controller.address.country = props.businessIdentityTByBiId.ctrCountry;
  
          if (props.businessIdentityTByBiId.ctrPassportNumber || props.businessIdentityTByBiId.ctrPassportCountry) {
            requestBody.controller.passport = {};
            requestBody.controller.passport.number = props.businessIdentityTByBiId.ctrPassportNumber;
            requestBody.controller.passport.country = props.businessIdentityTByBiId.ctrPassportCountry;
          }
        }



      }
      return requestBody;
    }
   
    render() {
        const { validateEmail, isAuthenticated, isAuthenticatedReseller, needAssent, logError } = this.props.auth;
        const classes = this.props.classes;
        const statusOk = green[300];
        const statusWaiting = amber[400];
        const statusRetry = red[300];
    

        if (!isAuthenticated() || needAssent()) {
          return (
            <Redirect to='/home' />
          );
        } else if (isAuthenticatedReseller()) {


          const params = new URLSearchParams(this.props.location.search);
          let biId = params.get('biId');
    
          let regionLabel = "Region";

          let emailError = false;
          if (this.state.contactEmail != null && this.state.contactEmail !== '') {
            let emailOk = validateEmail(this.state.contactEmail);
            if (! emailOk) {
              emailError = true;
            }
          }                  

          let dwollaCid = params.get('dId');
          let protectContact = false;
          if (dwollaCid) {
            protectContact = true;
          }

          //console.log(JSON.stringify(this.state.existingDwollaCust));

          return (
            <QueryRenderer
              environment={abEnvironment.getEnvironment()}
              query={BusinessIdentityEditQuery}
              variables={{biId: biId, forceFetch: this.state.forceFetch}}
              render={({error, props})=> {
                if (error) {
                  logError(error.message);
                  return (
                    <ServerError/>
                  );
                } else if (props) {


                  if (!this.state.dwollaFetchDone) { 

                    return (
                      <div className={classes.root}>
                        <Grid container direction="column" justifyContent="center">
                          <Grid item>
                            <Grid container spacing={0} direction="row" justifyContent="center" className={classes.spinny}>
                              <Grid item>
                                <CircularProgress color="primary" size={75} thickness={2}/>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </div>
                    );
            
                  } else {

                    let address = {
                      country:    this.getValueForRender(this.state.country,    props.businessIdentityTByBiId.country, ''),
                      state:      this.getValueForRender(this.state.state,    props.businessIdentityTByBiId.state, ''),
                      city:       this.getValueForRender(this.state.city,    props.businessIdentityTByBiId.city, ''),
                      postalCode: this.getValueForRender(this.state.postalCode,    props.businessIdentityTByBiId.postalCode, ''),
                      address1:   this.getValueForRender(this.state.address1,    props.businessIdentityTByBiId.address1, ''),
                      address2:   this.getValueForRender(this.state.address2,    props.businessIdentityTByBiId.address2, '')
                    };
                    let ctrAddress = {
                      country:    this.getValueForRender(this.state.ctrCountry,    props.businessIdentityTByBiId.ctrCountry, ''),
                      state:      this.getValueForRender(this.state.ctrState,    props.businessIdentityTByBiId.ctrState, ''),
                      city:       this.getValueForRender(this.state.ctrCity,    props.businessIdentityTByBiId.ctrCity, ''),
                      postalCode: this.getValueForRender(this.state.ctrPostalCode,    props.businessIdentityTByBiId.ctrPostalCode, ''),
                      address1:   this.getValueForRender(this.state.ctrAddress1,    props.businessIdentityTByBiId.ctrAddress1, ''),
                      address2:   this.getValueForRender(this.state.ctrAddress2,    props.businessIdentityTByBiId.ctrAddress2, '')
                    };

                    // Determine the maximum date.  So 18 years ago
                    let theMaxDate = subYears(new Date(), 18);
          
                    let einErrorText = '';
                    if (this.getValueForRender(this.state.businessType, props.businessIdentityTByBiId.businessType, null) !== 'soleProprietorship') {
                      if (this.getValueForRender(this.state.ein, props.businessIdentityTByBiId.ein, null) !== null) {
                        let ein = this.getValueForRender(this.state.ein, props.businessIdentityTByBiId.ein, null);
                        //validate ein is 9
                        var re = /^\d{9}$/;
                        if (ein && !re.test(ein)) {
                          einErrorText = 'EIN must be 9 numbers no dashes'
                        }
                      }
                    }

                    let adminName = this.getValueForRender(this.state.contactFirstName, props.businessIdentityTByBiId.contactFirstName) + ' ' + this.getValueForRender(this.state.contactLastName, props.businessIdentityTByBiId.contactLastName) 

                    //you can only add owners once business has been sent to Dwolla
                    let addOwnerAllowed = false;
                    if (props.businessIdentityTByBiId.accountTByAccountId.dwollaCid) {
                      if (this.state.existingDwollaCust && (this.state.existingDwollaCust.status === 'unverified')) {
                        addOwnerAllowed = false;
                      } else if (!this.state.existingDwollaCust) {
                        addOwnerAllowed = false;
                      } else {
                        addOwnerAllowed = true;
                      }
                    }

                    let sendDwollaAllowed = true;
                    let uploadDwollaAllowed = false;
                    if (this.state.existingDwollaCust && (this.state.existingDwollaCust.status === 'document' ||  this.state.existingDwollaCust.status === 'suspended')) {
                      sendDwollaAllowed = false;
                    }
                    if (this.state.existingDwollaCust && (this.state.existingDwollaCust.status === 'document')) {
                      uploadDwollaAllowed = true;
                    }
                    if (this.state.existingDwollaCust && (this.state.existingDwollaCust.status === 'verified')) {
                      sendDwollaAllowed = false;
                    }

                    let verificationStatus = null;
                    let uploadInstructions = '';  
                    let noChange = false;
                    if (this.state.existingDwollaCust) {
                      if (this.state.existingDwollaCust.status === 'verified') {
                        verificationStatus = 'Verified';
                        noChange = true;
                      }
                      if (this.state.existingDwollaCust.status === 'document') {
                        verificationStatus = 'Need documentation';
                        if (this.getValueForRender(this.state.businessType, props.businessIdentityTByBiId.businessType, null) === 'soleProprietorship') {
                          uploadInstructions = 'Need to upload one or more of the following, as applicable to your sole proprietorship: Fictitious Business Name Statement; Certificate of Assumed Name; Business License; Sales/Use Tax License; Registration of Trade Name; EIN documentation (IRS-issued SS4 confirmation letter); Color copy of a valid government-issued photo ID (e.g., a driver’s license, passport, or state ID card).';  
                        } else  {
                          uploadInstructions = 'Need to upload EIN Letter (IRS-issued SS4 confirmation letter)';
                        } 
                        noChange = true;
                      }
                      if (this.state.dwollaStatus === 'retry') {
                        verificationStatus = 'Retry';
                      }
                      
                    }

                    //see if there are any failed documents.  If there are, a link is presented to take them to a new page.
                    var failedDocuments = false;
                    if (props.businessIdentityTByBiId.businessIdentityDocTsByBiId.totalCount > 0) {
                      for (var i = 0; i < props.businessIdentityTByBiId.businessIdentityDocTsByBiId.totalCount; i++) {
                        var doc = props.businessIdentityTByBiId.businessIdentityDocTsByBiId.nodes[i];
                        if (doc.dwollaStatus === 'Failed Approval') {
                          failedDocuments = true;
                          break;
                        }
                      }
                    }

                    let soleP = (this.getValueForRender(this.state.businessType, props.businessIdentityTByBiId.businessType, 'soleProprietorship') === 'soleProprietorship');

                    let certifyNeeded = false;
                    if (this.state.certifyOwnersNeeded === true && props.businessIdentityTByBiId.beneficialOwnerTsByBiId.totalCount > 0) {

                      //console.log(this.state.certifyOwnersNeeded);
                      if (verificationStatus === 'Verified') {
                        certifyNeeded = true;
                      }
                    }

                    //console.log(certifyNeeded);

                    //console.log(this.disableSave(props) );
                    return (
                      <div id="business-identity-edit" className={classes.root}>
                        <PageTitle title="Edit your business identity" showBackButton={true} name="Company info" history={this.props.history}/>
      
                        <Paper className={classes.paper}>
                          <Grid container spacing={2} direction="row" justifyContent="flex-start">
                          {verificationStatus && !certifyNeeded &&
                            <Grid item xs={12}>
                              <Grid container alignItems="center">
                                <Grid item>
                                  <TextField  
                                    id="status" label="Verification status"
                                    value={  verificationStatus}
                                    InputProps={{ readOnly: true, disableUnderline: true }}
                                  />
                                </Grid>
                                <Grid item>
                                  {(this.state.existingDwollaCust.status === 'verified') &&
                                    <CheckCircle htmlColor={statusOk} style={{verticalAlign: "-20"}}/>
                                  }
                                  {(this.state.existingDwollaCust.status === 'retry') &&
                                    <Cancel htmlColor={statusRetry} style={{verticalAlign: "-20"}}/>
                                  }
                                  {(this.state.existingDwollaCust.status === 'document') &&
                                    <Tooltip title={uploadInstructions}>
                                      <HourglassEmpty htmlColor={statusWaiting}  style={{verticalAlign: "-20"}}/>
                                    </Tooltip>
                                  }
                                </Grid>
    
                              </Grid>
                            </Grid>
                            }                     
                          {verificationStatus && certifyNeeded &&
                            <React.Fragment>
                            <Grid item xs={12} sm={6}>
                              <Grid container alignItems="center">
                                <Grid item>
                                  <TextField  
                                    id="status" label="Verification status"
                                    value={  verificationStatus}
                                    InputProps={{ readOnly: true, disableUnderline: true }}
                                  />
                                </Grid>
                                <Grid item>
                                  {(this.state.existingDwollaCust.status === 'verified') &&
                                    <CheckCircle htmlColor={statusOk} style={{verticalAlign: "-20"}}/>
                                  }
                                  {(this.state.existingDwollaCust.status === 'retry') &&
                                    <Cancel htmlColor={statusRetry} style={{verticalAlign: "-20"}}/>
                                  }
                                  {(this.state.existingDwollaCust.status === 'document') &&
                                    <Tooltip title={uploadInstructions}>
                                      <HourglassEmpty htmlColor={statusWaiting}  style={{verticalAlign: "-20"}}/>
                                    </Tooltip>
                                  }
                                </Grid>
    
                              </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Button
                                style={{marginTop: 16}}
                                color="primary" variant="outlined"
                                onClick={() => { this.setState({certifyOwnersDialog:true}); }}
                              >
                                Certify&nbsp;owners
                              </Button>
                            </Grid>
                            </React.Fragment>
                            }                     
          
                          <Grid item xs={12} sm={6}>
                              <FormControl variant="standard" required className={classes.formControl}>
                                <InputLabel id="business-type">Business type</InputLabel>
                                <Select
                                  labelId="dbusiness-type-select-required-label"
                                  id="business-type-select-required"
                                  value={this.getValueForRender(this.state.businessType, props.businessIdentityTByBiId.businessType, '')}
                                  onChange={this.handleBusinessChange}
                                  disabled={noChange}
                                >
                                  <MenuItem value={'soleProprietorship'}>Sole proprietorship</MenuItem>
                                  <MenuItem value={'corporation'}>Corporation</MenuItem>
                                  <MenuItem value={'llc'}>Limited liability corporation</MenuItem>
                                  <MenuItem value={'partnership'}>Partnership</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>

                          <Grid item xs={12}>
                              <Typography variant="h6" style={AppBindStyles.sectionTitleFirst}>
                                Account
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                id="business-name" label="Business name *"
                                value={ this.getValueForRender(this.state.businessName, props.businessIdentityTByBiId.businessName, '')}
                                onChange={ (e) => this.setState({ businessName: e.target.value }) }
                                inputProps={{ maxLength: 64 }}
                                InputProps={{ readOnly: noChange, disableUnderline: noChange }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                              id="dba" label="Doing business as *"
                              value={  this.getValueForRender(this.state.doingBusinessAs, props.businessIdentityTByBiId.doingBusinessAs, '')}
                              onChange={ (e) => this.setState({ doingBusinessAs: e.target.value }) }
                              inputProps={{ maxLength: 64 }}
                            />
                          </Grid>
          
                            { !soleP &&
                              <Grid item xs={12} sm={6}>
                                <TextField data-recording-ignore="mask"
                                id="ein" label="EIN (no dashes) *"
                                value={  this.getValueForRender(this.state.ein, props.businessIdentityTByBiId.ein, '')}
                                onChange={ (e) => this.setState({ ein: e.target.value }) }
                                inputProps={{ maxLength: 9 }}
                                InputProps={{ readOnly: noChange, disableUnderline: noChange }}
                                error={einErrorText !== ''}
                                helperText={einErrorText}
                                />
                              </Grid>
                            }
                          <Grid item xs={12} sm={6}>
                              <TextField
                                id="website" label="Web site"
                                value={  this.getValueForRender(this.state.website, props.businessIdentityTByBiId.website, '')}
                                onChange={ (e) => this.setState({ website: e.target.value }) }
                                inputProps={{ maxLength: 128 }}
                              />
                            </Grid>
                          <Grid>
                              <FormControl variant="standard"
                                className={classes.businessClassification}
                                >
                                <InputLabel shrink htmlFor="businessClassification">Business classification *</InputLabel>
                                <NativeSelect
                                  value={this.getValueForRender(this.state.businessClassification, props.businessIdentityTByBiId.businessClassification, '')}
                                  input={<Input name="businessClassification" id="country" />}
                                  onChange={(e) => this.setState({ businessClassification: e.target.value })}
                                  className={classes.nselect}
                                  disabled={noChange}
                                  >
                                  <option key="" value="">Select a classification</option>
                                  {this.state.dwollaList.map(n => {
                                    return(<option key={n.id} value={n.id}>{n.name}</option>);
                                  })}
                                </NativeSelect>
                              </FormControl>
                          </Grid>
                        
                            <Grid item xs={12}>
                              <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                                Primary contact
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField  data-recording-ignore="mask"
                                id="first-name" label="First name *"
                                value={  this.getValueForRender(this.state.contactFirstName, props.businessIdentityTByBiId.contactFirstName, '')}
                                onChange={ (e) => this.setState({ contactFirstName: e.target.value }) }
                                inputProps={{ maxLength: 32 }}
                                InputProps={{ readOnly: protectContact, disableUnderline: protectContact }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField  data-recording-ignore="mask"
                                id="last-name" label="Last name *"
                                value={  this.getValueForRender(this.state.contactLastName, props.businessIdentityTByBiId.contactLastName, '')}
                                onChange={ (e) => this.setState({ contactLastName: e.target.value }) }
                                inputProps={{ maxLength: 32 }}
                                InputProps={{ readOnly: protectContact, disableUnderline: protectContact }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                            
                              <TextField  data-recording-ignore="mask"
                                id="email" label="Email *"
                                error={emailError}
                                value={  this.getValueForRender(this.state.contactEmail, props.businessIdentityTByBiId.contactEmail, '')}
                                onChange={ (e) => this.setState({ contactEmail: e.target.value }) }
                                InputProps={{ readOnly: protectContact, disableUnderline: protectContact }}
                              />
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                              <TextField  data-recording-ignore="mask"
                                id="phone" label="Contact phone" 
                                value={  this.getValueForRender(this.state.contactPhone, props.businessIdentityTByBiId.contactPhone, '')}
                                onChange={ (e) => this.setState({ contactPhone: e.target.value }) }
                                InputProps={{
                                  inputComponent: PhoneNumberFormat,
                                }}
                              />
                            </Grid>
                            { soleP &&
                            <Grid item xs={12} sm={6}>
                              <div style={AppBindStyles.datePickerField}>
                              <DatePicker data-recording-ignore="mask"
                                  autoOk
                                  label="Date of birth *"
                                  value={this.getValueForRender(this.state.dateOfBirth, props.businessIdentityTByBiId.dateOfBirth, null)}
                                  onChange={(date) => this.setState({ dateOfBirth: date }) }
                                  className={classes.dateField}
                                  format="YYYY-MM-DD"
                                  maxDate={theMaxDate}
                                  maxDateMessage="Business owner must be over 18"
                                  renderInput={(params) => <TextField {...params} />}
                                />
                              </div>
                            </Grid>
                            }
                            { soleP && verificationStatus !== 'Retry' &&
                              <Grid item xs={12} sm={6}>
                                <TextField data-recording-ignore="mask"
                                id="ssn" label="Last 4 SSN *"
                                value={  this.getValueForRender(this.state.ssn, props.businessIdentityTByBiId.ssn, '') }
                                onChange={ (e) => this.setState({ ssn: e.target.value }) }
                              />
                              </Grid>
                            }
                            { soleP && verificationStatus === 'Retry' &&
                              <Grid item xs={12} sm={6}>
                                <TextField data-recording-ignore="mask"
                                id="ssn9" label="Full 9 digit SSN *"
                                value={  this.getValueForRender(this.state.ssn, props.businessIdentityTByBiId.ssn, '') }
                                onChange={ (e) => this.setState({ ssn: e.target.value }) }
                              />
                              </Grid>
                            }
                            <Grid item xs={12}>
                              <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                                Business mailing address
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Address
                                showRequired={true}
                                address={address}
                                regionLabel={regionLabel}
                                setCountryCodeState={this.setCountry}
                                setRegionCodeState={this.setRegion}
                                setCityState={this.setCity}
                                setPostalCodeState={this.setPostalCode}
                                setAddress1State={this.setAddress1}
                                setAddress2State={this.setAddress2}
                              />
                            </Grid>
                            { !soleP &&
                            <React.Fragment>
                              <Grid item xs={12}>
                                <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                                  Controller
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField  data-recording-ignore="mask"
                                  id="ctr-first-name" label="First name *"
                                  value={ this.getValueForRender(this.state.ctrFirstName, props.businessIdentityTByBiId.ctrFirstName, '') }
                                  onChange={ (e) => this.setState({ ctrFirstName: e.target.value }) }
                                  inputProps={{ maxLength: 32 }}
                                  InputProps={{ readOnly: noChange, disableUnderline: noChange }}
                                  />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField  data-recording-ignore="mask"
                                  id="ctr-last-name" label="Last name *"
                                  value={ this.getValueForRender(this.state.ctrLastName, props.businessIdentityTByBiId.ctrLastName, '') }
                                  onChange={ (e) => this.setState({ ctrLastName: e.target.value }) }
                                  inputProps={{ maxLength: 32 }}
                                  InputProps={{ readOnly: noChange, disableUnderline: noChange }}
                                  />
                              </Grid>                         
                              <Grid item xs={12} sm={6}>
                                <div style={AppBindStyles.datePickerField}>
                                <DatePicker data-recording-ignore="mask"
                                  autoOk
                                  label="Date of birth *"
                                  value={this.getValueForRender(this.state.ctrDateOfBirth, props.businessIdentityTByBiId.ctrDateOfBirth, null)}
                                  onChange={(date) => this.setState({ ctrDateOfBirth: date }) }
                                  className={classes.dateField}
                                  format="YYYY-MM-DD"
                                  maxDate={theMaxDate}
                                  maxDateMessage="Business owner must be over 18"
                                  InputProps={{ readOnly: noChange, disableUnderline: noChange }}
                                  renderInput={(params) => <TextField {...params} />}
                                />
                                </div>
                              </Grid>
                              {verificationStatus !== 'Retry' &&
                              <Grid item xs={12} sm={6}>
                                  <TextField data-recording-ignore="mask"
                                  id="ctr-ssn" label={"Last 4 SSN " + ((this.getValueForRender(this.state.ctrCountry, props.businessIdentityTByBiId.ctrCountry, '') === "US") ? " *"  : "")}
                                  value={ this.getValueForRender(this.state.ctrSsn, props.businessIdentityTByBiId.ctrSsn, '') }
                                  onChange={ (e) => this.setState({ ctrSsn: e.target.value }) }
                                  InputProps={{ readOnly: noChange, disableUnderline: noChange }}
                                  />
                              </Grid>
                              }
                              {verificationStatus === 'Retry' &&
                              <Grid item xs={12} sm={6}>
                                  <TextField data-recording-ignore="mask"
                                  id="ctr-ssn" label={"Full 9 digit SSN " + ((this.getValueForRender(this.state.ctrCountry, props.businessIdentityTByBiId.ctrCountry, '') === "US") ? " *"  : "")}
                                  value={ this.getValueForRender(this.state.ctrSsn, props.businessIdentityTByBiId.ctrSsn, '') }
                                  onChange={ (e) => this.setState({ ctrSsn: e.target.value }) }
                                  InputProps={{ readOnly: noChange, disableUnderline: noChange }}
                                  />
                              </Grid>
                              }
                              <Grid item xs={12} sm={6}>
                                  <TextField 
                                  id="ctr-title" label="Title *"
                                  value={ this.getValueForRender(this.state.ctrTitle, props.businessIdentityTByBiId.ctrTitle, '') }
                                  onChange={ (e) => this.setState({ ctrTitle: e.target.value }) }
                                  InputProps={{ readOnly: noChange, disableUnderline: noChange }}
                                  />
                              </Grid>
                              <Grid item xs={12}>
                                <Address
                                  showRequired={true}
                                  address={ctrAddress}
                                  displayOnly={noChange}
                                  regionLabel={regionLabel}
                                  setCountryCodeState={this.setCtrCountry}
                                  setRegionCodeState={this.setCtrRegion}
                                  setCityState={this.setCtrCity}
                                  setPostalCodeState={this.setCtrPostalCode}
                                  setAddress1State={this.setCtrAddress1}
                                  setAddress2State={this.setCtrAddress2}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                  <TextField data-recording-ignore="mask"
                                  id="ppnumber" label={"Passport number" + ((this.getValueForRender(this.state.ctrCountry, props.businessIdentityTByBiId.ctrCountry, '') !== "US") ? " *"  : "")}
                                  value={ this.getValueForRender(this.state.ctrPassportNumber, props.businessIdentityTByBiId.ctrPassportNumber, '') }
                                  onChange={ (e) => this.setState({ ctrPassportNumber: e.target.value }) }
                                  InputProps={{ readOnly: noChange, disableUnderline: noChange }}
                                  />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                              <div className={classes.select}>
                                <CountrySelect data-recording-ignore="mask"
                                  overrideLabel={"Passport country"}
                                  displayOnly={noChange}
                                  showRequired={this.getValueForRender(this.state.ctrCountry, props.businessIdentityTByBiId.ctrCountry, '') !== "US"}
                                  countryCode={this.getValueForRender(this.state.ctrPassportCountry, props.businessIdentityTByBiId.ctrPassportCountry, '')}
                                  setCountryCodeState={this.setPassportCountry}
                                  
                                />
                              </div>
                              </Grid>
                              <Grid item xs={12}>
                                <Grid container spacing={1} alignItems="center">
                                  <Grid item>
                                    <Typography variant="h6" style={AppBindStyles.sectionTitleTable}>
                                      Beneficial owners
                                    </Typography>
                                  </Grid>
                                  <Grid item>
                                    <Tooltip placement='right' title='We require disclosing of key individuals who ultimately own or control a legal entity and own more than 25% stake.  If your business is exempt or there are no individuals with at least 25% ownership, this is not required.'>
                                        <HelpOutline fontSize="small"  style={{verticalAlign: "-22"}}/>
                                    </Tooltip>
                                  </Grid>
                                </Grid>
                              </Grid>

                              <Grid item xs={12}>
                                <TableContainer>
                                    <Table>
                                      <TableHead className={classes.tblHead}>
                                        <TableRow>
                                          <TableCell className={classes.tableCellPadding}>First name</TableCell>
                                          <TableCell className={classes.tableCellPadding}>Last name</TableCell>
                                          <TableCell className={classes.tableCellPadding}>Sent to ACH partner?</TableCell>
                                          <TableCell className={classes.tableCellPadding}>
                                            <Button
                                              className={classes.iconbutton}
                                              color="primary" variant="outlined"
                                              disabled={!(this.disableSave(props) && addOwnerAllowed)}
                                              onClick={this.goToBeneficialOwnerCreate.bind(this, biId)}
                                            >
                                              <Add className={classes.addicon}/>&nbsp;Add&nbsp;owner
                                            </Button>
                                          </TableCell>
                                      </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {props.businessIdentityTByBiId.beneficialOwnerTsByBiId.nodes.map(n => {
                                          let tlink = 'businessbeneficialowneredit?boId=' + n.boId;
                                          if (n.dwollaId) {
                                            tlink = tlink + '&dId=' + n.dwollaId;
                                          }
                                          return (
                                            <TableRow key={n.boId}>
                                              <TableCell className={classes.tableCellPadding} data-recording-sensitive>{n.firstName}</TableCell>
                                              <TableCell className={classes.tableCellPadding} data-recording-sensitive> {n.lastName}</TableCell>
                                              <TableCell className={classes.tableCellPadding} data-recording-sensitive> {(n.dwollaId) ? 'Yes' : 'No'}</TableCell>
                                              <TableCell className={classes.tableCellPadding}>
                                                <Button
                                                  className={classes.iconbutton}
                                                  disabled={!this.disableSave(props)}
                                                  onClick={this.goTo.bind(this, tlink)}>
                                                  <Edit className={classes.editicon}/>
                                                </Button>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>

                              </Grid>
                            </React.Fragment>
                            }
          
                            {props.businessIdentityTByBiId.businessIdentityDocTsByBiId.totalCount > 0 &&
                            <React.Fragment>
                              <Grid item xs={12}>
                                <Grid container spacing={1} alignItems="center">
                                  <Grid item>
                                    <Typography variant="h6" style={AppBindStyles.sectionTitleTable}>
                                      Documents uploaded
                                    </Typography>
                                  </Grid>
                                  <Grid item>
                                    <Tooltip placement='right' title='Our ACH provider requires 1 document and they are usually reviewed in 1-2 business days.  If the document uploaded does not transmit or the correct type, they may require more than one.'>
                                        <HelpOutline fontSize="small"  style={{verticalAlign: "-22"}}/>
                                    </Tooltip>
                                  </Grid>
                                </Grid>
                              </Grid>

                              <Grid item xs={12}>
                                <TableContainer>
                                    <Table>
                                      <TableHead className={classes.tblHead}>
                                        <TableRow>
                                          <TableCell className={classes.tableCellPadding}>Document type</TableCell>
                                          <TableCell className={classes.tableCellPadding}>Date uploaded</TableCell>
                                          <TableCell className={classes.tableCellPadding}>Status</TableCell>
                                          {failedDocuments &&
                                          <TableCell className={classes.tableCellPadding}>Details</TableCell>
                                          }
                                      </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {props.businessIdentityTByBiId.businessIdentityDocTsByBiId.nodes.map(n => {

                                          var documentType = '';
                                          if (n.documentType === 0) {
                                            documentType = 'EIN letter';
                                          } else if (n.documentType === 1) {
                                            documentType = 'Fictitious Business Name Statement'
                                          } else if (n.documentType === 2) {
                                            documentType = 'Certificate of Assumed Name'
                                          } else if (n.documentType === 3) {
                                            documentType = 'Business License'
                                          } else if (n.documentType === 4) {
                                            documentType = 'Sales/Use Tax License'
                                          } else if (n.documentType === 5) {
                                            documentType = 'Registration of Trade Name'
                                          } else if (n.documentType === 6) {
                                            documentType = 'Government-issued photo ID'
                                          }
                                          let created = format(new Date(n.created+'Z'), 'yyyy-MM-dd pp');
                                          
                                          return(
                                            <TableRow key={n.bidId}>
                                              <TableCell className={classes.tableCellPadding}>{documentType}</TableCell>
                                              <TableCell className={classes.tableCellPadding}>{created}</TableCell>
                                              <TableCell className={classes.tableCellPadding}>{n.dwollaStatus}</TableCell>
                                              {failedDocuments && (n.dwollaStatus === 'Failed Approval') &&
                                              <TableCell className={classes.tableCellPadding}>
                                                <Button
                                                    className={classes.iconbutton}
                                                    color="primary"
                                                    onClick={() => {
                                                      this.goToDocumentDetail(n.bidId, n.dwollaId)
                                                      }}>
                                                  <ZoomIn className={classes.detailIcon}/>
                                                </Button>
                                              </TableCell>
                                              }
                                              {failedDocuments && (n.dwollaStatus !== 'Failed Approval') &&
                                              <TableCell className={classes.tableCellPadding}>
                                              </TableCell>
                                              }

                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>

                              </Grid>
                              </React.Fragment>
                            }

                                  <ConfirmationDialog
                                      open={this.state.certifyOwnersDialog}
                                      onClose={() => { this.setState({certifyOwnersDialog:false});}}
                                      onConfirm={() => { this.sendOwnerCerification(biId); }}
                                      onCancel={() => { }}
                                      irreversibleAction={true}
                                      title="Certify owner(s)?"
                                      cancelBtn="Cancel"
                                      okBtn="Ok"
                                    >
                                    { 'I, ' + adminName + ', hereby certify, to the best of my knowledge, that the information provided is complete and correct.'}
                                  </ConfirmationDialog>  
                            <Grid item>

                              <SaveButton
                                      snackbarOpen={this.state.saveSnackbarOpen}
                                      autoHideDuration={this.state.saveSnackbarDuration}
                                      onSaveClick={() => { this._updateBusinessEntity(biId); }}
                                      onSnackbarClose={() => { this._onSnackbarClose(); }}
                                      status={this.state.saveStatus}
                                      message={this.state.saveMessage}
                                      disabled={this.disableSave(props)}/>      
                            
                                {uploadDwollaAllowed &&
                                <Button color="primary" variant="outlined" style={AppBindStyles.bottomButton}
                                  disabled={!this.disableSave(props)}
                                onClick={() => { this.goToDocumentUpload(biId); }}
                                  >
                                  upload document
                                </Button>
                                }

                                {sendDwollaAllowed && !this.showSecondarySpinner() &&
                                <Button color="primary" variant="outlined" style={AppBindStyles.bottomButton}
                                  disabled={!this.disableSave(props)}
                                        onClick={() => {this.setState({confirmSend:true});}}>
                                  Send to ACH Partner
                                </Button> }

                                {sendDwollaAllowed && this.showSecondarySpinner() &&
                                <Button color="primary" variant="outlined" style={AppBindStyles.bottomButton}>
                                  <SavingSpinner/>
                                </Button>
                                }

                              </Grid>
                              <ConfirmationDialog
                                  open={this.state.confirmSend}
                                  onClose={() => { this.setState({confirmSend:false});}}
                                  onConfirm={() => {this.sendDwollaInfo(biId, props) }}
                                  onCancel={() => { }}
                                  title="Confirm information?"
                                  cancelBtn="Cancel"
                                  okBtn="Ok"
                                  irreversibleAction={true}
                                >
                                  Please verify all your information.  If you proceed, the information will be sent to our ACH partner for verification and most information will not be able to be changed.  Is information complete?
                            </ConfirmationDialog>  
                          </Grid>
                        </Paper>
                      </div>
                    );
                  }
                }
                return <div><LoadingSpinner/></div>
              }}
            />
          );

       } else {
          return (
            <Redirect to='/home' />
          );
        }
    }

    _updateBusinessEntity = (biId) => {

      //if (abEnvironment.isSegmentActive()) { window.analytics.track('Customer create saving customer'); }   
      const { logError } = this.props.auth;
 
      this.setState({ showSpinner: true });
      const { businessName, doingBusinessAs, businessType, businessClassification, website, ein, ipAddress, ssn, dateOfBirth,
        contactLastName, contactFirstName, contactEmail, contactPhone, address1, address2, city, state, country, postalCode,
        ctrTitle, ctrSsn, ctrDateOfBirth, ctrPassportNumber, ctrPassportCountry, ctrLastName, ctrFirstName, 
        ctrAddress1, ctrAddress2, ctrCity, ctrState, ctrCountry, ctrPostalCode } = this.state;
      
      //let theBiId = null;

      BusinessIdentityUpdateMutation.commit(biId, businessName, doingBusinessAs, businessType, businessClassification, website, ein, ipAddress, ssn, dateOfBirth,
        contactLastName, contactFirstName, contactEmail, contactPhone, address1, address2, city, state, country, postalCode,
        ctrTitle, ctrSsn, ctrDateOfBirth, ctrPassportNumber, ctrPassportCountry, ctrLastName, ctrFirstName, 
        ctrAddress1, ctrAddress2, ctrCity, ctrState, ctrCountry, ctrPostalCode)
      .then(response => {
        //theBiId = response.updateBusinessIdentityTByBiId.businessIdentityT.biId;
 
        
        return null;
      })
      .then(response => {
        let fetchCounter = this.state.forceFetch;
        fetchCounter++;
        this.setState({ forceFetch: fetchCounter, showSpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Business identity updated.', saveSnackbarDuration: 3000 });
      })
      .catch(error => {
        logError('Exception in BusinessIdentifyEdit.js: ' + error);
        this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Unable to save business identity.' });
        // this.goBack();
      });
    }

    _onSnackbarClose() {
      this.setState( {saveSnackbarOpen: false, saveStatus: '', saveMessage: '', saveSnackbarDuration: null });
    }

}

BusinessIdentityEdit.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(BusinessIdentityEdit);